export interface VennInput {
  names: string[];
  sets: Set<string>[];
}

/**
 * Parse pasted columns of items into named sets. The first row is the header
 * (set names); each column below it is that set's members (blank cells skipped).
 * Accepts comma-, tab- or (multi-)space-separated columns.
 */
export function parseVennSets(text: string): VennInput {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+$/, ""))
    .filter((l) => l.trim() !== "");
  if (lines.length === 0) return { names: [], sets: [] };

  const split = (l: string) =>
    (/[,\t]/.test(l) ? l.split(/[,\t]/) : l.split(/ {2,}/)).map((c) =>
      c.trim(),
    );
  const header = split(lines[0]);
  const cols = header.length;
  const sets: Set<string>[] = header.map(() => new Set<string>());

  for (let r = 1; r < lines.length; r++) {
    const cells = split(lines[r]);
    for (let c = 0; c < cols; c++) {
      const v = (cells[c] ?? "").trim();
      if (v !== "") sets[c].add(v);
    }
  }
  return { names: header.map((h) => h || "Set"), sets };
}

export interface VennRegions {
  count: number; // number of sets (2 or 3)
  // region key → count
  regions: Record<string, number>;
  sizes: number[];
}

const inter = (a: Set<string>, b: Set<string>) =>
  [...a].filter((x) => b.has(x)).length;

/** Compute region counts for a 2- or 3-set Venn diagram. */
export function vennRegions(input: VennInput): VennRegions | null {
  const { sets } = input;
  if (sets.length < 2 || sets.length > 3) return null;
  const sizes = sets.map((s) => s.size);

  if (sets.length === 2) {
    const [A, B] = sets;
    const ab = inter(A, B);
    return {
      count: 2,
      sizes,
      regions: { A: A.size - ab, B: B.size - ab, AB: ab },
    };
  }

  const [A, B, C] = sets;
  const abc = [...A].filter((x) => B.has(x) && C.has(x)).length;
  const ab = inter(A, B) - abc;
  const ac = inter(A, C) - abc;
  const bc = inter(B, C) - abc;
  return {
    count: 3,
    sizes,
    regions: {
      A: A.size - ab - ac - abc,
      B: B.size - ab - bc - abc,
      C: C.size - ac - bc - abc,
      AB: ab,
      AC: ac,
      BC: bc,
      ABC: abc,
    },
  };
}
